import React, { useEffect, lazy, Suspense } from "react";
import axios from "axios";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext.jsx";
import LayoutAuthenticated from "./components/layout/LayoutAuthenticated.jsx";
import LayoutGuest from "./components/layout/LayoutGuest.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import { hasRole } from "./roles.js";

// Lazy-loaded pages (will be filled as phases complete)
const CasesView     = lazy(() => import("./pages/CasesView.jsx").catch(() => ({ default: () => <FallbackPage title="Casos" /> })));
const CatalogView   = lazy(() => import("./pages/CatalogView.jsx").catch(() => ({ default: () => <FallbackPage title="Catálogo" /> })));
const MetricsView   = lazy(() => import("./pages/MetricsView.jsx").catch(() => ({ default: () => <FallbackPage title="Métricas" /> })));
const AlertsView    = lazy(() => import("./pages/AlertsView.jsx").catch(() => ({ default: () => <FallbackPage title="Alertas" /> })));
const SettingsView  = lazy(() => import("./pages/SettingsView.jsx").catch(() => ({ default: () => <FallbackPage title="Configuración" /> })));
const UsersView     = lazy(() => import("./pages/UsersView.jsx").catch(() => ({ default: () => <FallbackPage title="Usuarios" /> })));
const AuditView     = lazy(() => import("./pages/AuditView.jsx").catch(() => ({ default: () => <FallbackPage title="Auditoría" /> })));
const ProfileView   = lazy(() => import("./pages/ProfileView.jsx").catch(() => ({ default: () => <FallbackPage title="Perfil" /> })));

const API_BASE = import.meta.env.VITE_AGENT_API_URL || "/api";

function FallbackPage({ title }) {
  return (
    <div className="flex items-center justify-center h-64 text-gray-400 dark:text-gray-500">
      <p className="text-sm">{title} — próximamente</p>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, status } = useAuth();

  useEffect(() => {
    const token = localStorage.getItem("agent_token");
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  }, [user]);

  if (status !== "ready") return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RoleRoute({ roles, children }) {
  const { user, status } = useAuth();
  if (status !== "ready") return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasRole(user, roles)) return <Navigate to="/cases" replace />;
  return children;
}

function RoutesContainer() {
  useEffect(() => {
    axios.defaults.baseURL = API_BASE;
  }, []);

  return (
    <Routes>
      {/* Guest layout */}
      <Route element={<LayoutGuest />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      {/* Authenticated layout */}
      <Route
        element={
          <ProtectedRoute>
            <LayoutAuthenticated />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/cases" replace />} />
        <Route path="/cases" element={<Suspense fallback={<LoadingSpinner />}><CasesView /></Suspense>} />
        <Route path="/catalog" element={<RoleRoute roles="admin"><Suspense fallback={<LoadingSpinner />}><CatalogView /></Suspense></RoleRoute>} />
        <Route path="/metrics" element={<RoleRoute roles={["admin", "agent_l2"]}><Suspense fallback={<LoadingSpinner />}><MetricsView /></Suspense></RoleRoute>} />
        <Route path="/alerts" element={<RoleRoute roles={["admin", "agent_l2"]}><Suspense fallback={<LoadingSpinner />}><AlertsView /></Suspense></RoleRoute>} />
        <Route path="/settings" element={<RoleRoute roles="admin"><Suspense fallback={<LoadingSpinner />}><SettingsView /></Suspense></RoleRoute>} />
        <Route path="/users" element={<RoleRoute roles="admin"><Suspense fallback={<LoadingSpinner />}><UsersView /></Suspense></RoleRoute>} />
        <Route path="/audit" element={<RoleRoute roles={["admin", "agent_l2"]}><Suspense fallback={<LoadingSpinner />}><AuditView /></Suspense></RoleRoute>} />
        <Route path="/profile" element={<Suspense fallback={<LoadingSpinner />}><ProfileView /></Suspense>} />
      </Route>

      {/* Fallback — must be outside authenticated group so it doesn't match /login */}
      <Route path="*" element={<Navigate to="/cases" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <RoutesContainer />
    </AuthProvider>
  );
}
